import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  LineChart,
  Brain,
  Scale,
  TrendingUp,
  Sparkles,
  Activity,
} from "lucide-react";
import LandingSandbox from "../components/LandingSandbox";
import MarketRadar from "../components/MarketRadar";

/* ---------- Dark futuristic landing — scoped (does not affect rest of app) ---------- */

const MODELED_AFTER = [
  { name: "Nomura", note: "Asia-pacific equity research + quantitative price-target methodology" },
  { name: "Goldman Sachs", note: "Global Investment Research valuation framework + macro scenarios" },
  { name: "Morgan Stanley", note: "Industry-specific blue-sky / grey-sky scenario analysis" },
  { name: "UBS", note: "Quantitative DCF modelling + global wealth-tier equity coverage" },
  { name: "J.P. Morgan", note: "Cross-asset consensus + commodities-linked sector flows" },
];
const CROSS_REFERENCED = [
  { name: "Bloomberg", note: "Real-time consensus estimates · the industry gold standard" },
  { name: "FactSet", note: "Granular financial modelling + ownership data" },
  { name: "Visible Alpha", note: "Line-item sell-side consensus extracted at the assumption layer" },
  { name: "Refinitiv", note: "LSEG Workspace · historical macro + I/B/E/S consensus" },
];

const FEATURE_TABS = [
  {
    key: "fair-value",
    icon: Scale,
    title: "Fair Value",
    desc:
      "Analyst consensus reconciled with an AI-driven independent DCF — same stock, two views, side-by-side.",
    detail:
      "Live yfinance targets, Morningstar fair values, TickerTape and SimplyWall.st intrinsics, and a Claude Sonnet 4.5 DCF — median of all five. The spread is the signal.",
    stat: "5",
    statLabel: "Sources reconciled",
  },
  {
    key: "ai-thesis",
    icon: Brain,
    title: "AI Thesis",
    desc:
      "Bull case, bear case, confidence, and industry outlook — written by an LLM trained on equity research.",
    detail:
      "Each snapshot ships with a Claude-generated thesis that cross-checks Screener.in pros & cons against the model's own DCF. You get the bear case before you buy, not after.",
    stat: "2",
    statLabel: "Bull / Bear",
  },
  {
    key: "analyst-panel",
    icon: LineChart,
    title: "Analyst Panel",
    desc: "Live breakdown of Strong Buy / Buy / Hold / Sell across every covering analyst.",
    detail:
      "Yahoo Finance recommendation feed, refreshed hourly. See whether the street is herding or split — and how that lines up with your fair-value verdict.",
    stat: "01h",
    statLabel: "Refresh cadence",
  },
  {
    key: "ranked-compare",
    icon: TrendingUp,
    title: "Ranked Compare",
    desc: "Compare up to 8 tickers side-by-side, ranked by Margin of Safety descending.",
    detail:
      "Drop your shortlist, hit Compare, and the highest-conviction undervalued names rise to the top. Sort any column — Yahoo FV, Morningstar FV, consolidated FV, live price diff.",
    stat: "08",
    statLabel: "Tickers / board",
  },
];

/* Premium hover-tooltip badge — pure CSS, no library */
function PartnerBadge({ name, note, variant = "muted" }) {
  const muted =
    "border-white/10 bg-white/[0.03] text-white/70 hover:text-white hover:border-emerald-400/30";
  const accent =
    "border-emerald-400/15 bg-emerald-400/[0.04] text-emerald-300/80 hover:text-emerald-200 hover:border-emerald-400/40";
  const cls = variant === "accent" ? accent : muted;
  const testId = `partner-${name.toLowerCase().replace(/[\s.]+/g, "-")}`;
  return (
    <span className="group relative inline-block" data-testid={testId}>
      <span
        className={`inline-flex items-center px-3 py-1.5 rounded-[3px] border backdrop-blur-md text-[0.7rem] tracking-wide transition-all cursor-default ${cls}`}
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        tabIndex={0}
        aria-describedby={`${testId}-note`}
      >
        {name}
      </span>
      {/* Tooltip */}
      <span
        id={`${testId}-note`}
        role="tooltip"
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 z-30 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0 transition-all duration-200"
      >
        <span className="block bg-[#0B0F19]/95 backdrop-blur-xl border border-white/15 rounded-[4px] p-3 shadow-2xl shadow-black/40">
          <span
            className="block text-[0.7rem] font-semibold text-white mb-1"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            {name}
          </span>
          <span className="block text-[0.65rem] text-white/55 leading-relaxed">{note}</span>
        </span>
      </span>
    </span>
  );
}

/* Dark navbar — minimalist, glass, scroll-aware */
function DarkNav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0B0F19]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-white/5"
      }`}
      data-testid="dark-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-14 sm:h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 sm:gap-2.5 group" data-testid="dark-navbar-logo">
          <div className="w-7 h-7 border border-white/20 bg-white/[0.04] flex items-center justify-center rounded-[3px] group-hover:border-emerald-400/60 transition-colors">
            <LineChart className="w-3.5 h-3.5 text-emerald-400" />
          </div>
          <div className="leading-tight">
            <div
              className="text-[14px] sm:text-[15px] font-semibold text-white tracking-tight"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Fairvest
            </div>
            <div className="font-mono text-[0.5rem] sm:text-[0.55rem] uppercase tracking-[0.25em] text-white/40">
              Equity Research
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <Link
            to="/login"
            className="inline-flex items-center px-3 sm:px-4 py-2 text-xs font-medium text-white/70 hover:text-white transition-colors"
            data-testid="dark-nav-login"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Sign in
          </Link>
          <Link to="/signup" data-testid="dark-nav-signup">
            <button
              className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-[3px] text-xs font-semibold text-[#0B0F19] bg-emerald-400 hover:bg-emerald-300 transition-colors"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              <span className="hidden sm:inline">Open terminal</span>
              <span className="sm:hidden">Sign up</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </Link>
        </div>
      </div>
    </header>
  );
}

export default function Landing() {
  const [activeTab, setActiveTab] = useState(FEATURE_TABS[0].key);
  const active = FEATURE_TABS.find((t) => t.key === activeTab) || FEATURE_TABS[0];
  const ActiveIcon = active.icon;

  return (
    <div
      className="min-h-screen bg-[#0B0F19] text-white antialiased relative overflow-hidden"
      style={{ fontFamily: "'Space Grotesk', 'Manrope', sans-serif" }}
      data-testid="landing-page"
    >
      {/* Subtle radial halos */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60rem 30rem at 15% 10%, rgba(52, 211, 153, 0.08), transparent 70%), radial-gradient(50rem 30rem at 85% 35%, rgba(99, 102, 241, 0.06), transparent 70%)",
        }}
      />
      {/* Faint grid overlay */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <DarkNav />

      <div className="relative">
        {/* Today's most undervalued & growth stocks — PINNED AT TOP */}
        <section
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-8 sm:pt-10 lg:pt-16 pb-4"
          data-testid="landing-radar-section"
        >
          <div className="mb-4 sm:mb-6">
            <div className="overline mb-2 sm:mb-3 text-emerald-400 font-mono text-[0.6rem] sm:text-[0.65rem] uppercase tracking-[0.3em]">
              — Live · updated every 10 min · no sign-up needed
            </div>
            <h1 className="font-serif-display text-2xl sm:text-4xl lg:text-5xl text-white font-medium tracking-[-0.02em] mb-2 leading-tight">
              Today's most undervalued
              <span className="text-white/40"> & </span>
              <span className="text-emerald-400">growth stocks</span>
            </h1>
            <p className="text-white/50 text-xs sm:text-sm max-w-2xl">
              Live scan of Nifty 50 (India) &amp; US mega-caps — refreshed every 10 min. Tap any name for the full 6-source valuation.
            </p>
          </div>
          <div className="rounded-[3px] overflow-hidden shadow-2xl shadow-emerald-500/5">
            <MarketRadar />
          </div>
        </section>

        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-12">
          {/* Micro-status bar (terminal feel) */}
          <div className="flex items-center gap-3 mb-10" data-testid="hero-status">
            <span className="inline-flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.25em] text-emerald-400">
              <span className="relative flex w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-emerald-400/50 animate-ping" />
                <span className="relative w-2 h-2 rounded-full bg-emerald-400" />
              </span>
              Live · NYSE · NASDAQ · NSE · BSE
            </span>
            <span className="h-3 w-px bg-white/10" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-white/40">
              v2.4 · Feb 2026
            </span>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">
            {/* Left column — copy */}
            <div className="lg:col-span-7">
              <h1 className="text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-[-0.025em] font-semibold text-white mb-5 sm:mb-6">
                Find the
                <br />
                <span className="text-emerald-400">undervalued.</span>{" "}
                <span className="text-white/30">Skip the noise.</span>
              </h1>
              <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-xl mb-8 sm:mb-10">
                Fairvest reconciles fair-value estimates, analyst consensus, and industry growth context
                across US{" "}
                <span className="font-mono text-emerald-400/90">NASDAQ / NYSE</span> and Indian{" "}
                <span className="font-mono text-emerald-400/90">NSE / BSE</span> — then pairs them with an
                independent AI valuation to surface what the market has mispriced.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 mb-10 sm:mb-14">
                <Link to="/signup" data-testid="hero-signup-btn" className="w-full sm:w-auto">
                  <button className="w-full sm:w-auto group inline-flex items-center justify-center gap-2 bg-emerald-400 hover:bg-emerald-300 text-[#0B0F19] rounded-[3px] px-7 py-3.5 text-sm font-semibold transition-colors">
                    Start researching
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </Link>
                <Link to="/login" data-testid="hero-login-link" className="w-full sm:w-auto">
                  <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/[0.03] backdrop-blur-md border border-white/15 hover:border-white/30 hover:bg-white/[0.06] text-white/80 hover:text-white rounded-[3px] px-7 py-3.5 text-sm font-medium transition-colors">
                    I already have an account
                  </button>
                </Link>
              </div>

              {/* Data partners ticker */}
              <div data-testid="data-partners-row" className="space-y-5">
                <div>
                  <div className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-white/30 mb-3">
                    — Modeled to emulate institutional research from
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    {MODELED_AFTER.map((p) => (
                      <PartnerBadge key={p.name} {...p} variant="muted" />
                    ))}
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-white/30 mb-3">
                    — Cross-referenced against
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    {CROSS_REFERENCED.map((p) => (
                      <PartnerBadge key={p.name} {...p} variant="accent" />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right column — sample card */}
            <div className="lg:col-span-5 relative" data-testid="hero-sample-card-wrap">
              {/* Glow */}
              <div
                aria-hidden="true"
                className="absolute -inset-6 rounded-2xl opacity-40 blur-3xl"
                style={{
                  background:
                    "radial-gradient(circle at 30% 20%, rgba(52, 211, 153, 0.35), transparent 60%), radial-gradient(circle at 70% 80%, rgba(99, 102, 241, 0.2), transparent 60%)",
                }}
              />
              <div
                className="relative bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[6px] p-7 hover:border-white/20 transition-all"
                data-testid="hero-sample-card"
              >
                {/* Card header */}
                <div className="flex items-start justify-between mb-7">
                  <div>
                    <div className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-white/40 mb-2">
                      Fair value · sample
                    </div>
                    <div
                      className="text-3xl font-semibold tracking-tight text-white"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      NVDA
                    </div>
                    <div className="text-xs text-white/40 mt-0.5">NVIDIA Corporation · NASDAQ</div>
                  </div>
                  <span className="px-2.5 py-1 rounded-[3px] text-[0.6rem] font-mono font-semibold uppercase tracking-[0.2em] bg-emerald-400/15 text-emerald-300 border border-emerald-400/30">
                    Undervalued
                  </span>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-2 gap-5 mb-5">
                  <div className="border-l border-white/10 pl-4">
                    <div className="font-mono text-[0.6rem] uppercase tracking-[0.25em] text-white/40 mb-2">
                      Analyst Mean
                    </div>
                    <div className="font-mono text-2xl text-white tabular-nums">$182.30</div>
                  </div>
                  <div className="border-l border-emerald-400/40 pl-4">
                    <div className="font-mono text-[0.6rem] uppercase tracking-[0.25em] text-emerald-300/70 mb-2">
                      AI Fair Value
                    </div>
                    <div className="font-mono text-2xl text-emerald-400 tabular-nums">$195.00</div>
                  </div>
                </div>

                {/* Highlight tile */}
                <div className="bg-emerald-400/[0.06] border border-emerald-400/20 rounded-[4px] p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-[0.6rem] uppercase tracking-[0.25em] text-emerald-300/70">
                      Margin of Safety
                    </div>
                    <Activity className="w-3.5 h-3.5 text-emerald-300/70" />
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-4xl text-emerald-400 tabular-nums font-semibold">
                      +21.4%
                    </span>
                    <span className="text-[0.65rem] text-white/40 font-mono uppercase tracking-widest">
                      vs current
                    </span>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
                  <span className="font-mono text-[0.55rem] uppercase tracking-[0.25em] text-white/35">
                    Live · 5 sources reconciled
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-mono text-[0.55rem] uppercase tracking-[0.25em] text-emerald-400/80">
                    <span className="relative flex w-1.5 h-1.5">
                      <span className="absolute inset-0 rounded-full bg-emerald-400/60 animate-ping" />
                      <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    </span>
                    Streaming
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Anonymous sandbox — no login required */}
        <LandingSandbox />

        {/* Stat strip */}
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 border-y border-white/5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { k: "US + IN", v: "4 exchanges" },
              { k: "5", v: "FV sources reconciled" },
              { k: "AI", v: "Claude Sonnet 4.5 DCF" },
              { k: "0%", v: "Emotional bias" },
            ].map((s, i) => (
              <div key={i} className="flex flex-col gap-2">
                <div className="font-mono text-3xl text-white tabular-nums tracking-tight">{s.k}</div>
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-white/40">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Features — horizontal tabs */}
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28" data-testid="features-section">
          <div className="text-center mb-14">
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-emerald-400/80 mb-4 inline-flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> What Fairvest gives you
            </div>
            <h2 className="text-4xl sm:text-5xl text-white tracking-[-0.02em] font-semibold">
              Four lenses on a single stock.
            </h2>
          </div>

          {/* Tab strip */}
          <div
            role="tablist"
            aria-label="Fairvest features"
            className="flex flex-wrap items-center justify-center gap-2 mb-10"
            data-testid="feature-tablist"
          >
            {FEATURE_TABS.map((t) => {
              const Icon = t.icon;
              const isActive = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`panel-${t.key}`}
                  id={`tab-${t.key}`}
                  onClick={() => setActiveTab(t.key)}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-[3px] text-sm font-medium border transition-all ${
                    isActive
                      ? "bg-emerald-400 text-[#0B0F19] border-emerald-400 shadow-[0_0_24px_-6px_rgba(52,211,153,0.5)]"
                      : "bg-white/[0.03] backdrop-blur-md text-white/70 border-white/10 hover:border-white/30 hover:text-white"
                  }`}
                  data-testid={`feature-tab-${t.key}`}
                >
                  <Icon className="w-4 h-4" />
                  {t.title}
                </button>
              );
            })}
          </div>

          {/* Panel */}
          <div
            role="tabpanel"
            id={`panel-${active.key}`}
            aria-labelledby={`tab-${active.key}`}
            className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-[6px] p-8 lg:p-12 grid lg:grid-cols-12 gap-10 items-center min-h-[280px]"
            data-testid={`feature-panel-${active.key}`}
            key={active.key}
          >
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-10 h-10 rounded-[3px] bg-emerald-400/15 border border-emerald-400/40 text-emerald-400 flex items-center justify-center">
                  <ActiveIcon className="w-5 h-5" />
                </span>
                <h3
                  className="text-3xl text-white font-semibold tracking-tight"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {active.title}
                </h3>
              </div>
              <p className="text-white/75 text-lg leading-relaxed mb-4">{active.desc}</p>
              <p className="text-white/45 text-sm leading-relaxed max-w-2xl">{active.detail}</p>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-[#0B0F19]/60 backdrop-blur-xl border border-white/10 rounded-[4px] p-8 text-center">
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-white/40 mb-3">
                  {active.statLabel}
                </div>
                <div className="font-mono text-5xl sm:text-6xl text-emerald-400 font-semibold tracking-tight tabular-nums">
                  {active.stat}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sequential journey: stocks → metals → land */}
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 border-t border-white/5" data-testid="journey-section">
          <div className="mb-10">
            <div className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-emerald-400/80 mb-3">— One desk · three markets</div>
            <h2 className="text-lg md:text-lg font-medium text-white max-w-xl">
              Start with undervalued stocks, hedge with physical metal, anchor with land.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                n: "01",
                title: "Stocks — find the mispriced",
                desc: "6-source fair-value consensus, 2030/2040/2050 growth horizons, daily radar of quality fallers across India + US.",
                eg: "e.g. scan Nifty 50 for names trading 15%+ below consensus fair value",
                testid: "journey-stocks",
              },
              {
                n: "02",
                title: "Metals — buy the real thing",
                desc: "Official IBJA 24K rates, city jeweller board rates, expert 2030 targets overlaid on the real curve, assisted physical purchase.",
                eg: "e.g. compare your quoted shop rate against today's official ₹/10g before paying",
                testid: "journey-metals",
              },
              {
                n: "03",
                title: "Land — own cheap acres with water",
                desc: "AI farmland radar by location, budget-filtered live deal links, outsider-eligibility flags, and a lawyer-grade document checklist.",
                eg: "e.g. water-rich plots under ₹5L/acre within reach of your city",
                testid: "journey-land",
              },
            ].map((s) => (
              <div key={s.n} className="bg-white/[0.03] border border-white/10 rounded-[6px] p-6 hover:border-emerald-400/30 transition-colors" data-testid={s.testid}>
                <div className="font-mono text-emerald-400/70 text-sm mb-3">{s.n}</div>
                <div className="text-white font-medium mb-2">{s.title}</div>
                <p className="text-sm text-white/55 mb-3">{s.desc}</p>
                <p className="text-[0.7rem] font-mono text-white/35">{s.eg}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA strip */}
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="relative bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-[6px] p-10 lg:p-14 text-center overflow-hidden">
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-40"
              style={{
                background:
                  "radial-gradient(40rem 20rem at 50% 0%, rgba(52, 211, 153, 0.15), transparent 70%)",
              }}
            />
            <div className="relative">
              <h3
                className="text-3xl sm:text-4xl text-white font-semibold tracking-tight mb-3"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Open your terminal in 30 seconds.
              </h3>
              <p className="text-white/55 text-base max-w-xl mx-auto mb-7">
                Free during beta. No card. No nag. Just a clean read on what the market has mispriced.
              </p>
              <Link to="/signup" data-testid="cta-signup-btn">
                <button className="group inline-flex items-center gap-2 bg-emerald-400 hover:bg-emerald-300 text-[#0B0F19] rounded-[3px] px-7 py-3.5 text-sm font-semibold transition-colors">
                  Start researching
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/5 py-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-white/35">
              — Fairvest · Not investment advice
            </div>
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-white/35">
              Built for quiet conviction
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
